/**
 * What the browser would do on a page load, done by hand for a view change
 * (Website ADR 0003): announce it, move focus, retitle the tab.
 */

const SITE = "Better Streets Chicago Action Fund";

let live: HTMLElement | null = null;
let pending: ReturnType<typeof setTimeout> | undefined;

/**
 * One polite live region per mount, created once. Its inline style is the
 * contract's single exception to "no styles": a live region Webflow forgot to
 * hide would print every announcement on the page.
 */
export function ensureLiveRegion(mount: HTMLElement): HTMLElement {
  if (live && mount.contains(live)) return live;
  live = document.createElement("div");
  live.className = "bsc-elections__live";
  live.setAttribute("aria-live", "polite");
  live.setAttribute("aria-atomic", "true");
  live.setAttribute(
    "style",
    "position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0"
  );
  mount.prepend(live);
  return live;
}

/**
 * Announce once. Clearing first and writing on the next tick makes screen
 * readers speak a message even when it repeats the previous one.
 */
export function announce(message: string): void {
  if (!live) return;
  clearTimeout(pending);
  live.textContent = "";
  pending = setTimeout(() => {
    if (live) live.textContent = message;
  }, 60);
}

/** Focus the view's region without scrolling; scroll is restored separately. */
export function focusView(main: HTMLElement): void {
  main.setAttribute("tabindex", "-1");
  main.focus({ preventScroll: true });
}

/**
 * Install a synchronously rendered loading view without ever dropping focus
 * to body: insert next to the old view, focus it, then remove the old view.
 */
export function installLoadingView(
  next: HTMLElement,
  previous: HTMLElement | null,
  anchor: HTMLElement,
  shouldFocus: boolean
): void {
  if (previous) previous.after(next);
  else anchor.after(next);
  if (shouldFocus) focusView(next);
  previous?.remove();
}

export function setTitle(title: string): void {
  document.title = `${title} — Elections — ${SITE}`;
}
